import React, { useContext, useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Card,
  Table,
  Badge,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  getToken,
  sessionStorageGetItem,
  getMonetaryUnitText,
  getSelectedAddOnData,
  sessionStorageSetItem,
} from "../Function";
import {
  CurrentCurrencyContext,
  CurrencyContext,
  LoyaltyIDContext,
  VoucherCodeContext,
  VoucherCodeAirlineContext,
  InsuranceAddonContext,
  HealthCareAddOnContext,
  ValueMealAddonContext,
  LoyaltyDiscountContext,
  VoucherDiscountContext,
} from "../../UserContext";
import { LoadingScreenModal } from "../LoadingScreenModal";
import "./BookingSummary.css";
import CountdownTimer from "./CountdownTimer";
import moment from "moment";
import axios from "axios";

const safeJsonParse = (raw, fallback) => {
  try {
    const v = JSON.parse(raw);
    return v ?? fallback;
  } catch {
    return fallback;
  }
};

const toMoney = (n) => Number(n || 0).toFixed(2);

const BookingSummary = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  // Holds the payment URL when the server removed the loyalty discount (insufficient
  // points): we pause and warn the user before sending them to pay the full fare.
  const [loyaltyRemovedUrl, setLoyaltyRemovedUrl] = useState(null);

  // Contexts
  const { currentCurrency } = useContext(CurrentCurrencyContext);
  useContext(CurrencyContext);
  const { LoyaltyID } = useContext(LoyaltyIDContext);
  const { VoucherCode } = useContext(VoucherCodeContext);
  const { VoucherCodeAirlineNo } = useContext(VoucherCodeAirlineContext);
  const { VoucherDiscount, setVoucherDiscount } = useContext(
    VoucherDiscountContext,
  );
  const { insuranceAddOn } = useContext(InsuranceAddonContext);
  useContext(HealthCareAddOnContext);
  useContext(ValueMealAddonContext);
  const { TotalLoyalty } = useContext(LoyaltyDiscountContext);

  const token = getToken();
  const SERVER_URL = process.env.REACT_APP_SERVER_URL;
  const monetaryUnit = getMonetaryUnitText(currentCurrency);

  // sessionStorage
  const guests = safeJsonParse(
    sessionStorageGetItem("bookingGuestDetails") || "[]",
    [],
  );
  const contactInfo = safeJsonParse(
    sessionStorageGetItem("bookingContactInfo") || "{}",
    {},
  );
  const departData = safeJsonParse(
    sessionStorageGetItem("departData") || "null",
    null,
  );
  const returnData = safeJsonParse(
    sessionStorageGetItem("returnData") || "null",
    null,
  );
  const originData = safeJsonParse(
    sessionStorageGetItem("Origin_data") || "null",
    null,
  );
  const destinationData = safeJsonParse(
    sessionStorageGetItem("Destination_data") || "null",
    null,
  );

  const departTripKey = safeJsonParse(
    sessionStorageGetItem("depart_TripKey") || "null",
    null,
  );
  const returnTripKey = safeJsonParse(
    sessionStorageGetItem("return_TripKey") || "null",
    null,
  );
  const passengerCount = Number(sessionStorageGetItem("passengerCount") || "1");
  const rescheduleAmount = sessionStorageGetItem("rescheduleAmount");
  const seatDepartVersions = sessionStorageGetItem("seatDepartVersions");
  const sessionFacilityCharge = sessionStorageGetItem("facilitycharge");
  const sessionHandlingCharge = sessionStorageGetItem("handlingcharge");
  const sessionQrCharge = sessionStorageGetItem("qrcharge");

  const [qrCharge, setQrCharge] = useState(0);
  const [facilityCharge, setFacilityCharge] = useState(0);
  const [handlingCharge, setHandlingCharge] = useState(0);
  const [lockId, setLockId] = useState([]);
  const [returnLockId, setReturnLockId] = useState([]);

  const voucherCode = VoucherCode || "";

  // Addon data
  const selectedAddOnsRaw = getSelectedAddOnData();
  const selectedAddOns = Array.isArray(selectedAddOnsRaw)
    ? selectedAddOnsRaw
    : [];

  useEffect(() => {
    document.body.classList.add("booking-summary-page");

    setLoading(false);

    const handlePageShow = (event) => {
      if (event.persisted) {
        setLoading(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  // busFare parsing (session might be "40", 40, {"fare":40}, etc.)
  const rawBusFare = sessionStorageGetItem("busFare");

  const busFareValue = useMemo(() => {
    try {
      const parsed = JSON.parse(rawBusFare || "0");
      if (typeof parsed === "number") return parsed;
      if (typeof parsed === "string") return Number(parsed) || 0;
      if (parsed && typeof parsed === "object") {
        if (parsed.fare != null) return Number(parsed.fare) || 0;
        if (parsed.total != null) return Number(parsed.total) || 0;
      }
      return 0;
    } catch {
      return Number(rawBusFare) || 0;
    }
  }, [rawBusFare]);

  // Breakdown: Fare / Add-on / Discount / Final
  const addOnTotal = useMemo(() => {
    if (!Array.isArray(selectedAddOns) || selectedAddOns.length === 0) return 0;

    return selectedAddOns.reduce((sum, a) => {
      const v =
        Number(a?.totalamtinmyr) ||
        Number(a?.sub_totalPrice) ||
        Number(a?.amount) ||
        0;
      return sum + v;
    }, 0);
  }, [selectedAddOns]);

  const loyaltyDiscount = Number(TotalLoyalty) || 0;

  const voucherDiscountAmount = useMemo(() => {
    const raw = sessionStorageGetItem("voucherDiscountAmount");
    if (raw == null) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }, []);

  const discountTotal = Math.max(0, loyaltyDiscount + voucherDiscountAmount);

  const finalTotal = useMemo(() => {
    const x =
      (Number(busFareValue) || 0) +
      (Number(addOnTotal) || 0) -
      (Number(discountTotal) || 0) +
      Number(sessionQrCharge || 0) +
      Number(sessionHandlingCharge || 0) +
      Number(sessionFacilityCharge || 0);
    return Math.max(0, x);
  }, [
    busFareValue,
    addOnTotal,
    discountTotal,
    sessionQrCharge,
    sessionFacilityCharge,
    sessionHandlingCharge,
  ]);

  const [displayFinalTotal, setDisplayFinalTotal] = useState(finalTotal);
  const [displayBusFareValue, setDisplayBusFareValue] = useState(busFareValue);
  const [displayAddOnTotal, setDisplayAddOnTotal] = useState(addOnTotal);

  useEffect(() => {
    const addon = getSelectedAddOnData() || [];
    const departBusFare = JSON.parse(
      sessionStorageGetItem("departBusFare") || '{"fare":0,"sgdFare":0}',
    );
    const returnBusFare = JSON.parse(
      sessionStorageGetItem("returnBusFare") || '{"fare":0,"sgdFare":0}',
    );

    const fare =
      currentCurrency === "MYR"
        ? departBusFare.fare + returnBusFare.fare
        : departBusFare.sgdFare + returnBusFare.sgdFare;
    const addonTotal =
      addon.length > 0
        ? addon
            .map((a) =>
              currentCurrency === "MYR"
                ? Number(a.totalamtinmyr)
                : Number(a.totalamtinsgd),
            )
            .reduce((a, b) => a + b)
        : 0;

    setDisplayBusFareValue(fare.toFixed(2));
    setDisplayAddOnTotal(addonTotal.toFixed(2));
    setDisplayFinalTotal(
      (
        fare +
        addonTotal -
        discountTotal +
        Number(facilityCharge) +
        Number(handlingCharge) +
        Number(qrCharge)
      ).toFixed(2),
    );

    // setDisplayDiscountTotal(Number(loyalty) + Number(voucher));
  }, [
    currentCurrency,
    discountTotal,
    facilityCharge,
    handlingCharge,
    qrCharge,
  ]);

  // Validation
  useEffect(() => {
    if (!guests?.length || !departData || !originData || !destinationData) {
      alert("Missing booking details. Redirecting to home.");
      navigate("/");
    }
  }, [guests, departData, originData, destinationData, navigate]);

  const routeTitle = `${originData?.subplacedisplayname || ""} → ${destinationData?.subplacedisplayname || ""}`;
  const isReturnTrip = !!returnData;

  // Helpers for UI strings
  const departWhen =
    departData?.departure_time || departData?.DepartDate || "-";
  const returnWhen =
    returnData?.departure_time || returnData?.ReturnDate || "-";
  const departDuration = departData?.duration
    ? `${departData.duration} duration`
    : null;
  const returnDuration = returnData?.duration
    ? `${returnData.duration} duration`
    : null;

  useEffect(() => {
    const departGuests = [];
    const returnGuests = [];

    guests.forEach((guest) => {
      const guestObj = {
        PassengerName: guest.guest_name,
        PassengerLastName: "",
        PassengerIc: guest.ic,
        PassengerPassport: guest.passport,
        PassengerDateOfBirth: guest.dob,
        PassengerContact: contactInfo.contact,
        PassengerGender: guest.gender,
        PassengerType: guest.ticket_type,
        PassengerPassportExpiryDate: guest.passport_expiry_date,
        PassengerSalutation: guest.gender === "M" ? "MR" : "MRS/MS",
        PassengerTravelType: guest.ticket_type,
        PassengerNationality: guest.nationality,
        IsForeignPassenger: guest.nationality === "MY" ? false : true,
        PickupAddress: departData.pick_up,
        DropOffAddress: departData.drop_off,
        SeatTimeStamp: "",
        LockId: "",
        HasMeal: false,
        IsVegetarian: false,
      };

      if (guest.depart_seat_id) {
        departGuests.push({
          ...guestObj,
          SeatId: guest.depart_seat_id,
          SeatNumber: guest.depart_seat_number,
          SeatCode: guest.depart_seat_code,
          SeatDeck: guest.depart_seat_deck || 0,
        });
      }

      if (guest.return_seat_id) {
        returnGuests.push({
          ...guestObj,
          SeatId: guest.return_seat_id,
          SeatNumber: guest.return_seat_number,
          SeatCode: guest.return_seat_code,
          SeatDeck: guest.return_seat_deck || 0,
        });
      }
    });

    async function reserveSeat() {
      let totalQrCharge = 0;
      let totalHandlingCharge = 0;
      let totalFacilityCharge = 0;

      if (departData.operator === "HANDAL") {
        const requestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify({
            Currency: "MYR",
            TripKey: departData.TripKey,
            Passengers: departGuests,
            Trip: {
              Operator: departData.operator,
              TripKey: departData.TripKey,
              ShuttleBusTripKey: departData.ShuttleBusTripKey,
              DepartureDate: moment(departData.departure_time).format(
                "DD/MM/YYYY",
              ),
              CompanyId: departData.companyId,
              CompanyName: departData.companyName,
              CoachType: departData.class,
              CoachId: departData.coachId,
              CoachNumber: departData.coachNumber,

              FromPlaceId: originData.placeid,
              FromPlaceDisplayName: originData.placedisplayname,
              FromPlaceUniqueShortName: originData.placeuniqueshortname,
              FromSubPlaceId: originData.subplaceid,
              FromSubPlaceDisplayName: originData.subplacedisplayname,
              FromSubPlaceDisplayName1: originData.subplacedisplayname1,
              FromSubPlaceUniqueShortName: originData.subplaceuniqueshortname,

              ToPlaceId: destinationData.placeid,
              ToPlaceDisplayName: destinationData.placedisplayname,
              ToPlaceUniqueShortName: destinationData.placeuniqueshortname,
              ToSubPlaceId: destinationData.subplaceid,
              ToSubPlaceDisplayName: destinationData.subplacedisplayname,
              ToSubPlaceDisplayName1: destinationData.subplacedisplayname1,
              ToSubPlaceUniqueShortName:
                destinationData.subplaceuniqueshortname,

              FromLocationId: originData.placeid,
              ToLocationId: destinationData.placeid,
              FromStationId: originData.subplaceid,
              ToStationId: destinationData.subplaceid,

              Currency: "MYR",
              OriginalCurrency: "MYR",
              AdultPrice: departData.fare,
              ChildPrice: departData.child_fare,
              SeniorPrice: departData.child_fare,
              OkuPrice: departData.child_fare,
              AdultPriceInSGD: departData.adultPriceInSgd,
              ChildPriceInSGD: departData.childPriceInSgd,
              SeniorPriceInSGD: departData.childPriceInSgd,
              OkuPriceInSGD: departData.childPriceInSgd,

              TotalSeat: departGuests.length,
              SeatAvailable: departGuests.length,
              TripDuration: departData.duration,
              TripDurationInSecond: departData.duration_in_seconds,
              PickupAddress: departData.pick_up,
              DropOffAddress: departData.drop_off,
              RouteId: departData.routeId,
              TripDate: moment(departData.tripDate).format("DD/MM/YYYY"),
              TripTime: moment(departData.departure_time).format("HH:mm"),
              TripNo: departData.tripNo || "",
            },
            Version: 0,
            Operator: departData.operator,
          }),
        };

        try {
          const response = await fetch(
            `${SERVER_URL}/me/reserve/seat`,
            requestOptions,
          ).then(async (response) => {
            const result = await response.json();

            if (!response.ok) {
              throw result.message;
            }

            return result;
          });

          // A 200 without a lockid is still a failed reservation — storing it
          // would send LockIDs:[null] to order/new and confuse the customer
          // with the generic "15-minute limit" rejection later.
          if (!response.lockid) {
            throw "Seat reservation failed. Please select your seat(s) again.";
          }

          totalQrCharge += response.qrcharge || 0;
          totalHandlingCharge += response.handlingcharge || 0;
          totalFacilityCharge += response.facilitycharge || 0;

          setQrCharge(totalQrCharge);
          setHandlingCharge(totalHandlingCharge);
          setFacilityCharge(totalFacilityCharge);
          setLockId([response.lockid]);
          sessionStorageSetItem("lock_id", [response.lockid]);
          sessionStorageSetItem("qrcharge", totalQrCharge);
          sessionStorageSetItem("handlingcharge", totalHandlingCharge);
          sessionStorageSetItem("facilitycharge", totalFacilityCharge);
        } catch (error) {
          alert(error);
          // Leaving the customer on this page with no lock lets them pay for
          // seats that were never held — go back to seat selection instead.
          window.history.back();
          return;
        }

        if (returnGuests.length > 0) {
          const requestOptions = {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: token,
            },
            body: JSON.stringify({
              Currency: "MYR",
              TripKey: returnData.TripKey,
              Passengers: returnGuests,
              Trip: {
                Operator: returnData.operator,
                TripKey: returnData.TripKey,
                ShuttleBusTripKey: returnData.ShuttleBusTripKey,
                DepartureDate: moment(returnData.departure_time).format(
                  "DD/MM/YYYY",
                ),
                CompanyId: returnData.companyId,
                CompanyName: returnData.companyName,
                CoachType: returnData.class,
                CoachId: returnData.coachId,
                CoachNumber: returnData.coachNumber,

                FromPlaceId: destinationData.placeid,
                FromPlaceDisplayName: destinationData.placedisplayname,
                FromPlaceUniqueShortName: destinationData.placeuniqueshortname,
                FromSubPlaceId: destinationData.subplaceid,
                FromSubPlaceDisplayName: destinationData.subplacedisplayname,
                FromSubPlaceDisplayName1: destinationData.subplacedisplayname1,
                FromSubPlaceUniqueShortName:
                  destinationData.subplaceuniqueshortname,

                ToPlaceId: originData.placeid,
                ToPlaceDisplayName: originData.placedisplayname,
                ToPlaceUniqueShortName: originData.placeuniqueshortname,
                ToSubPlaceId: originData.subplaceid,
                ToSubPlaceDisplayName: originData.subplacedisplayname,
                ToSubPlaceDisplayName1: originData.subplacedisplayname1,
                ToSubPlaceUniqueShortName: originData.subplaceuniqueshortname,

                FromLocationId: destinationData.placeid,
                ToLocationId: originData.placeid,
                FromStationId: destinationData.subplaceid,
                ToStationId: originData.subplaceid,

                Currency: "MYR",
                OriginalCurrency: "MYR",
                AdultPrice: returnData.fare,
                ChildPrice: returnData.child_fare,
                SeniorPrice: returnData.child_fare,
                OkuPrice: returnData.child_fare,
                AdultPriceInSGD: returnData.adultPriceInSgd,
                ChildPriceInSGD: returnData.childPriceInSgd,
                SeniorPriceInSGD: returnData.childPriceInSgd,
                OkuPriceInSGD: returnData.childPriceInSgd,

                TotalSeat: returnGuests.length,
                SeatAvailable: returnGuests.length,
                TripDuration: returnData.duration,
                TripDurationInSecond: returnData.duration_in_seconds,
                PickupAddress: returnData.pick_up,
                DropOffAddress: returnData.drop_off,
                RouteId: returnData.routeId,
                TripDate: moment(returnData.tripDate).format("DD/MM/YYYY"),
                TripTime: moment(returnData.departure_time).format("HH:mm"),
                TripNo: returnData.tripNo || "",
              },
              Version: 0,
              Operator: returnData.operator,
            }),
          };

          try {
            const response = await fetch(
              `${SERVER_URL}/me/reserve/seat`,
              requestOptions,
            ).then(async (response) => {
              const result = await response.json();

              if (!response.ok) {
                throw result.message;
              }

              return result;
            });

            if (!response.lockid) {
              throw "Seat reservation failed. Please select your seat(s) again.";
            }

            totalQrCharge += response.qrcharge || 0;
            totalHandlingCharge += response.handlingcharge || 0;
            totalFacilityCharge += response.facilitycharge || 0;

            setQrCharge(totalQrCharge);
            setHandlingCharge(totalHandlingCharge);
            setFacilityCharge(totalFacilityCharge);
            setReturnLockId([response.lockid]);
            sessionStorageSetItem("return_lock_id", [response.lockid]);
            sessionStorageSetItem("qrcharge", totalQrCharge);
            sessionStorageSetItem("handlingcharge", totalHandlingCharge);
            sessionStorageSetItem("facilitycharge", totalFacilityCharge);
          } catch (error) {
            alert(error);
            // The depart lock is already stored at this point; going back to
            // seat selection clears it so the customer restarts with none stale.
            window.history.back();
            return;
          }
        }
      } else if (departData.operator === "YOYO") {
        const yoyoDepartArr = departGuests.map((d) => ({
          TripKey: departData.TripKey,
          SeatId: d.SeatId,
          SeatDeck: d.SeatDeck,
        }));

        const yoyoReturnArr = returnGuests.map((d) => ({
          TripKey: returnData.TripKey,
          SeatId: d.SeatId,
          SeatDeck: d.SeatDeck,
        }));

        try {
          const response = await Promise.all(
            yoyoDepartArr.map((d) =>
              axios.post(`${SERVER_URL}/yoyo/reserve/seat`, d, {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: token,
                },
              }),
            ),
          );

          const lockIds = response.map((r) => r.data.lockid);

          if (lockIds.some((id) => !id)) {
            throw new Error("Missing lock id in reserve response");
          }

          sessionStorageSetItem("lock_id", lockIds);

          if (yoyoReturnArr.length > 0) {
            const returnResponse = await Promise.all(
              yoyoReturnArr.map((d) =>
                axios.post(`${SERVER_URL}/yoyo/reserve/seat`, d, {
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: token,
                  },
                }),
              ),
            );

            const returnLockIds = returnResponse.map((r) => r.data.lockid);

            if (returnLockIds.some((id) => !id)) {
              throw new Error("Missing lock id in reserve response");
            }

            sessionStorageSetItem("return_lock_id", returnLockIds);
          }
        } catch (error) {
          alert(
            "Selected seat(s) might be booked or unavailable. Please select your seat(s) again.",
          );
          window.history.back();
          return;
        }
      }
    }

    const sessionLockId = sessionStorageGetItem("lock_id");
    const sessionReturnLockId = sessionStorageGetItem("return_lock_id");

    if (!sessionLockId) {
      reserveSeat();
    } else {
      setLockId(sessionLockId);
      setReturnLockId(sessionReturnLockId);
      setQrCharge(sessionStorageGetItem("qrcharge"));
      setHandlingCharge(sessionStorageGetItem("handlingcharge"));
      setFacilityCharge(sessionStorageGetItem("facilitycharge"));
    }
  }, []);

  const handleConfirmPayment = async () => {
    setLoading(true);

    const departGuests = [];
    const returnGuests = [];

    guests.forEach((guest) => {
      const guestObj = {
        ticket_type: guest.ticket_type,
        guest: guest.guest,
        guest_name: guest.guest_name,
        ic: guest.ic,
        passport: guest.passport,
        passport_expiry_date: guest.passport_expiry_date,
        dob: guest.dob,
        gender: guest.gender,
        nationality: guest.nationality,
      };

      if (guest.depart_seat_id) {
        departGuests.push({
          ...guestObj,
          depart_seat_id: guest.depart_seat_id,
          depart_seat_number: guest.depart_seat_number,
          depart_seat_code: guest.depart_seat_code,
          depart_seat_deck: guest.depart_seat_deck,
          depart_display_code: guest.depart_display_code,
        });
      } else {
        if (
          originData?.subplacedisplayname === "UITM" &&
          (destinationData?.subplacedisplayname ===
            "Terminal Shah Alam Section 17" ||
            destinationData?.subplacedisplayname === "Klang Sentral" ||
            destinationData?.subplacedisplayname ===
              "TBS (Terminal Bersepadu Selatan)")
        ) {
          departGuests.push({
            ...guestObj,
          });
        }
      }

      if (guest.return_seat_id) {
        returnGuests.push({
          ...guestObj,
          return_seat_id: guest.return_seat_id,
          return_seat_number: guest.return_seat_number,
          return_seat_code: guest.return_seat_code,
          return_seat_deck: guest.return_seat_deck,
          return_display_code: guest.return_display_code,
        });
      }
    });

    // The server accepts an order/new with an empty LockIDs array and creates a
    // paid order whose seats were never held (depart_lock_id NULL, empty cart
    // guid) — the customer pays but can never be issued a ticket. So if the lock
    // state is gone here (session cleared, mount-time re-reserve failed, expiry),
    // block the payment and send the customer back to reselect seats; SelectSeat
    // clears the stale lock keys on entry. The UITM open-seating flow is the one
    // flow that legitimately has no lock (the server skips seat locks for it).
    const isOpenSeatingFlow =
      originData?.subplacedisplayname === "UITM" &&
      (destinationData?.subplacedisplayname ===
        "Terminal Shah Alam Section 17" ||
        destinationData?.subplacedisplayname === "Klang Sentral" ||
        destinationData?.subplacedisplayname ===
          "TBS (Terminal Bersepadu Selatan)");

    if (!isOpenSeatingFlow) {
      const departLocks = sessionStorageGetItem("lock_id") || [];
      const returnLocks = sessionStorageGetItem("return_lock_id") || [];
      const locksValid =
        departLocks.length > 0 &&
        departLocks.every(Boolean) &&
        (returnGuests.length === 0 ||
          (returnLocks.length > 0 && returnLocks.every(Boolean)));

      if (!locksValid) {
        setLoading(false);
        alert(
          "Your seat reservation has expired or could not be completed. Please select your seat(s) again.",
        );
        window.history.back();
        return;
      }
    }

    const finalAddOnData = [...selectedAddOns];

    // keep your existing insurance push behavior
    const sessionInsurance = sessionStorageGetItem("insuranceAddOn");
    const insuranceData = insuranceAddOn?.fee
      ? insuranceAddOn
      : sessionInsurance
        ? safeJsonParse(sessionInsurance, { plan: "No Insurance", fee: 0 })
        : { plan: "No Insurance", fee: 0 };

    if (insuranceData?.plan && insuranceData.plan !== "No Insurance") {
      finalAddOnData.push({ insuranceAddOn: insuranceData });
    }

    async function sha512(text) {
      const encoder = new TextEncoder();
      const data = encoder.encode(text);

      const hashBuffer = await crypto.subtle.digest("SHA-512", data);

      const hashArray = Array.from(new Uint8Array(hashBuffer));

      const hashHex = hashArray
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

      return hashHex;
    }

    const requestOptions = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: token,
      },
      body: JSON.stringify({
        Amount: `${(busFareValue + qrCharge + facilityCharge + handlingCharge).toFixed(2)}`,
        Hash: await sha512(
          `${process.env.REACT_APP_TOKEN_CLIENT_SECRET}${departTripKey}${contactInfo.email}${(busFareValue + qrCharge + facilityCharge + handlingCharge).toFixed(2)}`,
        ),
        CustName: `${departGuests?.[0]?.guest_name || contactInfo.fullname || ""}`,
        CustEmail: `${contactInfo.email || ""}`,
        CustPhone: `${contactInfo.contact || ""}`,
        CustPhoneCountryCode: `+${contactInfo.countryCode || ""}`,
        Details: `${originData?.subplacedisplayname || ""} - ${destinationData?.subplacedisplayname || ""}`,
        CurrencyID: "MYR",

        DepartDate: `${departData?.DepartDate || departData?.departure_time?.split(" ")?.[0] || ""}`,
        DepartTime: `${departData?.departure_time?.split(" ")?.[1] || ""}`,
        DepartGuests: JSON.stringify(departGuests),
        DepartTripKey: `${departTripKey}`,
        DepartPlaceId: originData?.placeid,
        DepartPlaceName: originData?.placename,
        DepartSubPlaceId: originData?.subplaceid,
        DepartSubPlaceName: originData?.subplacename,
        DepartDuration: `${departData?.duration || ""}`,

        ReturnDate: returnData
          ? `${returnData?.ReturnDate || returnData?.departure_time?.split(" ")?.[0] || ""}`
          : "",
        ReturnTime: returnData
          ? `${returnData?.departure_time?.split(" ")?.[1] || ""}`
          : "",
        ReturnGuests:
          returnGuests.length > 0 ? JSON.stringify(returnGuests) : [],
        ReturnTripKey: returnTripKey ? `${returnTripKey}` : null,
        ReturnPlaceId: destinationData?.placeid,
        ReturnPlaceName: destinationData?.placename,
        ReturnSubPlaceId: destinationData?.subplaceid,
        ReturnSubPlaceName: destinationData?.subplacename,
        ReturnDuration: returnData ? returnData?.duration : null,

        RescheduleAmount: rescheduleAmount,
        OldCartGuid: originData?.OldCartGuid,
        VoucherCode: VoucherCode,
        NoOfPax: passengerCount,
        SessionID: sessionStorageGetItem("addOnSession"),
        LoyaltyID: LoyaltyID !== "0" ? LoyaltyID : null,
        AddOn: finalAddOnData.length > 0 ? finalAddOnData : null,
        BoardingCode: VoucherCodeAirlineNo,
        PrivateHireAmount: 0,
        LockIDs: [
          ...(sessionStorageGetItem("lock_id") || []),
          ...(sessionStorageGetItem("return_lock_id") || []),
        ],
        DepartVersion:
          seatDepartVersions?.find((d) => d.tripKey === departTripKey)
            ?.departVersion ?? 1,
        ReturnVersion:
          // Use ?? not ||: a valid seat-plan version of 0 must be preserved.
          // With ||, returnVersion 0 collapses to null, the backend stores
          // return_version NULL, and the return-leg seatConfirm fails with
          // "depart_version is required."
          seatDepartVersions?.find((d) => d.tripKey === returnTripKey)
            ?.returnVersion ?? null,

        QrCharge: Number(qrCharge).toFixed(2),
        FacilityCharge: Number(facilityCharge).toFixed(2),
        HandlingCharge: Number(handlingCharge).toFixed(2),
      }),
    };

    // console.log(requestOptions.body);

    fetch(`${SERVER_URL}/order/new`, requestOptions)
      .then((response) => response.json())
      .then((data) => {
        if (data.url) {
          // Server removed the loyalty discount (hold failed - insufficient points)
          // and re-priced the order to full fare. Warn before redirecting to pay.
          const discountRemoved =
            data.LoyaltyDiscountRemoved ?? data.loyaltyDiscountRemoved;
          if (discountRemoved) {
            setLoyaltyRemovedUrl(data.url);
            setLoading(false);
          } else {
            window.location.href = data.url;
          }
        } else {
          alert(data.msg || "Booking failed. Please try again.");
          setLoading(false);
        }
      })
      .catch(() => {
        alert("Error processing booking.");
        setLoading(false);
      });
  };

  return (
    <div className="tailwind-scope min-h-screen flex flex-col bg-[#f6f6f8] dark:bg-[#101622] font-display text-slate-900 dark:text-slate-100 antialiased">
      {loyaltyRemovedUrl && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-[#1a2233] rounded-2xl max-w-md w-full p-6 shadow-xl">
            <h3 className="text-lg font-bold text-[#d97706] mb-2">
              Loyalty discount not applied
            </h3>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-5">
              You don't have enough loyalty points to redeem this discount right now
              {loyaltyDiscount ? ` (RM${loyaltyDiscount} off)` : ""} — the points may
              have been used on another booking. Your booking will continue at the full
              fare, without the loyalty discount.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setLoyaltyRemovedUrl(null)}
                className="flex-1 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 py-3 rounded-xl font-bold"
                style={{ background: "none" }}
              >
                Go back
              </button>
              <button
                onClick={() => {
                  window.location.href = loyaltyRemovedUrl;
                }}
                className="flex-1 bg-[#135bec] text-white py-3 rounded-xl font-bold"
                style={{ border: "none" }}
              >
                Continue to payment
              </button>
            </div>
          </div>
        </div>
      )}
      <main className="flex-grow max-w-7xl mx-auto w-full px-4 md:px-10 py-8">
        {loading && <LoadingScreenModal />}

        <div className="mb-4">
          <CountdownTimer />
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column: Details */}
          <div className="flex-grow space-y-6">
            {/* Title Section */}
            <div className="space-y-1">
              <h2 className="text-3xl font-black tracking-tight text-slate-900 dark:text-slate-50">
                Booking Summary
              </h2>
              <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                <span className="font-semibold text-[#135bec]">
                  {originData?.subplacedisplayname || "Origin"}
                </span>
                <span className="material-symbols-outlined text-sm">
                  arrow_forward
                </span>
                <span className="font-semibold text-[#135bec]">
                  {destinationData?.subplacedisplayname || "Destination"}
                </span>
                <span className="mx-2 text-slate-300">|</span>
                <span className="bg-[#135bec]/10 text-[#135bec] px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider">
                  {isReturnTrip ? "Round Trip" : "One-Way"}
                </span>
              </div>
            </div>

            {/* Journey Card - Outbound */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
              <div className="p-3 border-slate-200 dark:border-slate-200 flex items-center gap-3 border-solid border-b border-t-0 border-l-0 border-r-0">
                <span className="material-symbols-outlined text-[#135bec]">
                  directions_bus
                </span>
                <h3 className="font-normal m-0 text-lg">
                  Journey Details (Outbound)
                </h3>
              </div>
              <div className="p-6">
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="flex-grow space-y-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                          Trip duration
                        </p>
                        <p className="text-lg font-bold">
                          {departDuration || "-"}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                          Departure Date
                        </p>
                        <p className="text-sm font-semibold">{departWhen}</p>
                      </div>
                    </div>
                    <div className="relative pl-6 space-y-6 before:content-[''] before:absolute before:left-[5px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-200 dark:before:bg-slate-700">
                      <div className="relative">
                        <div className="absolute -left-[25px] top-1 w-3 h-3 rounded-full bg-[#135bec] ring-4 ring-[#135bec]/20"></div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                          Departure
                          {departData?.departure_time?.split(" ")?.[1]
                            ? ` (${departData.departure_time.split(" ")[1]})`
                            : ""}
                        </p>
                        <p className="font-bold text-slate-800 dark:text-slate-200">
                          {originData?.subplacedisplayname || "Origin"}
                        </p>
                      </div>
                      <div className="relative">
                        <div className="absolute -left-[25px] top-1 w-3 h-3 rounded-full bg-slate-400 ring-4 ring-slate-100 dark:ring-slate-800"></div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                          Arrival
                          {departData?.arrival_time || departData?.date
                            ? ` (${departData.arrival_time || departData.date})`
                            : ""}
                        </p>
                        <p className="font-bold text-slate-800 dark:text-slate-200">
                          {destinationData?.subplacedisplayname ||
                            "Destination"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Journey Card - Return */}
            {isReturnTrip && (
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
                <div className="p-3 border-b border-solid border-t-0 border-l-0 border-r-0 border-slate-100 dark:border-slate-800 flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#135bec]">
                    directions_bus
                  </span>
                  <h3 className="font-normal m-0 text-lg">
                    Journey Details (Return)
                  </h3>
                </div>
                <div className="p-6">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-grow space-y-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                            Trip duration
                          </p>
                          <p className="text-lg font-bold">
                            {returnDuration || "-"}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                            Departure Date
                          </p>
                          <p className="text-sm font-semibold">{returnWhen}</p>
                        </div>
                      </div>
                      <div className="relative pl-6 space-y-6 before:content-[''] before:absolute before:left-[5px] before:top-2 before:bottom-2 before:w-[2px] before:bg-slate-200 dark:before:bg-slate-700">
                        <div className="relative">
                          <div className="absolute -left-[25px] top-1 w-3 h-3 rounded-full bg-[#135bec] ring-4 ring-[#135bec]/20"></div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                            Departure
                            {returnData?.departure_time?.split(" ")?.[1]
                              ? ` (${returnData.departure_time.split(" ")[1]})`
                              : ""}
                          </p>
                          <p className="font-bold text-slate-800 dark:text-slate-200">
                            {destinationData?.subplacedisplayname ||
                              "Destination"}
                          </p>
                        </div>
                        <div className="relative">
                          <div className="absolute -left-[25px] top-1 w-3 h-3 rounded-full bg-slate-400 ring-4 ring-slate-100 dark:ring-slate-800"></div>
                          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                            Arrival
                            {returnData?.arrival_time || returnData?.date
                              ? ` (${returnData.arrival_time || returnData.date})`
                              : ""}
                          </p>
                          <p className="font-bold text-slate-800 dark:text-slate-200">
                            {originData?.subplacedisplayname || "Origin"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Details Card */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="p-3 border-b border-solid border-t-0 border-l-0 border-r-0 border-slate-300 dark:border-slate-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="material-symbols-outlined text-[#135bec]">
                    person
                  </span>
                  <h3 className="font-normal text-lg m-0">Contact Details</h3>
                </div>
                {LoyaltyID && LoyaltyID !== "0" ? (
                  <span className="px-2 py-1 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 text-[10px] font-bold rounded uppercase tracking-wider flex items-center gap-1">
                    <span className="material-symbols-outlined text-xs">
                      grade
                    </span>{" "}
                    Loyalty ID: {LoyaltyID}
                  </span>
                ) : null}
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                    Full Name
                  </p>
                  <p className="font-bold">{guests?.[0]?.guest_name || "-"}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                    Email Address
                  </p>
                  <p className="font-bold">{contactInfo.email || "-"}</p>
                </div>
                <div>
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1">
                    Phone Number
                  </p>
                  <p className="font-bold">
                    +{contactInfo.countryCode}{" "}
                    {contactInfo.contact?.replace(contactInfo.countryCode, "")}
                  </p>
                </div>
              </div>
            </div>

            {/* Passenger Info Card */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
              <div className="p-3 border-b border-solid border-t-0 border-l-0 border-r-0 border-slate-100 dark:border-slate-800 flex items-center gap-3">
                <span className="material-symbols-outlined text-[#135bec]">
                  groups
                </span>
                <h3 className="font-normal text-lg m-0">
                  Passenger Information
                </h3>
                <span className="ml-auto bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-3 py-1 rounded-full text-xs font-bold">
                  {passengerCount} Passenger{passengerCount > 1 ? "s" : ""}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="bg-slate-50 dark:bg-slate-800/50">
                    <tr>
                      <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        #
                      </th>
                      <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Name
                      </th>
                      <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                        Type
                      </th>
                      <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">
                        Depart Seat
                      </th>
                      {isReturnTrip && (
                        <th className="px-6 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">
                          Return Seat
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {guests.map((g, i) => (
                      <tr key={i}>
                        <td className="px-6 py-4 font-bold text-slate-400">
                          {(i + 1).toString().padStart(2, "0")}
                        </td>
                        <td className="px-6 py-4">
                          <div className="font-bold">{g.guest_name}</div>
                          <div className="text-xs text-slate-500">
                            {g.gender}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 px-2 py-1 rounded text-xs font-bold">
                            {g.ticket_type === "A"
                              ? "Adult"
                              : g.ticket_type === "C"
                                ? "Child/OKU"
                                : g.ticket_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <span className="bg-[#135bec]/10 text-[#135bec] px-3 py-1 rounded-full text-sm font-bold">
                            {g.depart_seat_number}
                          </span>
                        </td>
                        {isReturnTrip && (
                          <td className="px-6 py-4 text-right">
                            <span className="bg-[#135bec]/10 text-[#135bec] px-3 py-1 rounded-full text-sm font-bold">
                              {g.return_seat_number}
                            </span>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Add-ons Card */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
              <div className="p-3 border-b border-solid border-r-0 border-l-0 border-top-0 border-slate-200 dark:border-slate-800 flex items-center gap-3">
                <span className="material-symbols-outlined text-[#135bec]">
                  loyalty
                </span>
                <h3 className="font-normal text-lg m-0">Add-ons & Discounts</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-slate-50 dark:border-slate-800">
                      <span className="font-bold text-slate-700 dark:text-slate-300">
                        Voucher Code
                      </span>
                      <span className="text-sm text-slate-500">
                        {voucherCode ? voucherCode : "No voucher applied"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-50 dark:border-slate-800">
                      <span className="font-bold text-slate-700 dark:text-slate-300">
                        Voucher Discount
                      </span>
                      <span className="font-bold">
                        - RM {toMoney(voucherDiscountAmount)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center py-2">
                      <span className="font-bold text-slate-700 dark:text-slate-300">
                        Loyalty Discount
                      </span>
                      <span className="font-bold">
                        - RM {toMoney(loyaltyDiscount)}
                      </span>
                    </div>
                  </div>

                  {selectedAddOns.length > 0 ? (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">
                        Selected Add-ons
                      </p>
                      <div className="space-y-3">
                        {selectedAddOns.map((a, idx) => (
                          <div
                            className="flex justify-between items-center py-2"
                            key={idx}
                          >
                            <span className="text-slate-700 dark:text-slate-300">
                              {a?.name || a?.addonname || "Add-on"}
                            </span>
                            <span className="font-bold">
                              {monetaryUnit}{" "}
                              {toMoney(
                                a?.totalamtinmyr ||
                                  a?.sub_totalPrice ||
                                  a?.amount ||
                                  0,
                              )}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="mt-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6 text-center">
                      <p className="text-sm text-slate-500 italic font-medium m-0">
                        No additional add-ons selected for this trip.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Sidebar Price Summary */}
          <div className="w-full lg:w-96">
            <div className="sticky top-6 space-y-6">
              <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
                <div className="p-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
                  <h3 className="font-black text-lg tracking-tight m-0">
                    Price Breakdown
                  </h3>
                </div>
                <div className="p-6 space-y-4" style={{ fontSize: "0.9em" }}>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 dark:text-slate-400">
                      Base Fare ({passengerCount}x Passenger)
                    </span>
                    <span className="font-bold">
                      {monetaryUnit} {displayBusFareValue}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 dark:text-slate-400">
                      QR Charge
                    </span>
                    <span className="font-bold">RM {toMoney(qrCharge)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 dark:text-slate-400 ">
                      Facility Charge
                    </span>
                    <span className="font-bold">
                      RM {toMoney(facilityCharge)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 dark:text-slate-400">
                      Handling Charge
                    </span>
                    <span className="font-bold">
                      RM {toMoney(handlingCharge)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-500 dark:text-slate-400">
                      Add-ons Total
                    </span>
                    <span className="font-bold">
                      {monetaryUnit} {displayAddOnTotal}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-emerald-600 dark:text-emerald-400">
                    <span>Total Discounts</span>
                    <span className="font-bold">
                      - RM {toMoney(discountTotal)}
                    </span>
                  </div>

                  <div className="pt-4 border-t border-slate-100 dark:border-slate-800">
                    <div className="flex justify-between items-end">
                      <div>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-0.5">
                          Grand Total
                        </p>
                        <p className="text-3xl font-black text-[#135bec] m-0">
                          {monetaryUnit} {displayFinalTotal}
                        </p>
                      </div>
                      <div className="text-[10px] text-slate-400 text-right">
                        Inclusive of all taxes
                      </div>
                    </div>
                  </div>

                  {currentCurrency === "SGD" && (
                    <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-3 rounded-lg text-xs flex gap-2 mt-4 items-start">
                      <span className="material-symbols-outlined text-sm">
                        info
                      </span>
                      <p>
                        Note: The final payment will be processed in MYR if your
                        selected currency is not MYR.
                      </p>
                    </div>
                  )}

                  <div className="space-y-3 pt-2">
                    <button
                      onClick={handleConfirmPayment}
                      className="w-full bg-[#135bec] text-white py-4 rounded-xl font-bold hover:bg-[#135bec]/90 transition-all shadow-lg shadow-[#135bec]/30 flex items-center justify-center gap-2"
                      style={{ border: "none", fontSize: "0.9em" }}
                    >
                      <span>Confirm & Pay</span>
                      <span className="material-symbols-outlined">
                        payments
                      </span>
                    </button>
                    <button
                      onClick={() => navigate(-1)}
                      className="w-full bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700 py-3 rounded-xl font-bold hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center gap-2"
                      style={{ fontSize: "0.9em" }}
                    >
                      <span className="material-symbols-outlined text-sm">
                        arrow_back
                      </span>
                      <span>Back to Seats</span>
                    </button>
                  </div>
                </div>
                <div className="bg-[#135bec]/5 dark:bg-[#135bec]/10 p-3 flex items-start gap-2">
                  <span className="material-symbols-outlined text-[#135bec] text-lg">
                    verified_user
                  </span>
                  <p className="text-xs text-slate-500 dark:text-slate-400 m-0 font-normal">
                    Secure payment gateway. Your transaction data is encrypted
                    and safe.
                  </p>
                </div>
              </div>

              {/* Mini Help */}
              <div className="bg-slate-100 dark:bg-slate-800/50 rounded-xl p-3 border border-dashed border-slate-300 dark:border-slate-700">
                <h4 className="font-bold text-sm mb-3">Need help?</h4>
                <ul className="space-y-2">
                  <li>
                    <a
                      href="https://www.causewaylink.com.my/help/"
                      className="text-sm text-slate-500 hover:text-[#135bec] flex items-center gap-2 no-underline"
                    >
                      <span className="material-symbols-outlined text-base">
                        help_outline
                      </span>{" "}
                      Booking Guide
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.causewaylink.com.my/company-information/contact-us/"
                      className="text-sm text-slate-500 hover:text-[#135bec] flex items-center gap-2 no-underline"
                    >
                      <span className="material-symbols-outlined text-base">
                        support_agent
                      </span>{" "}
                      Chat with Support
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default BookingSummary;
